<template>
  <dl v-for="(el, index) in list" :key="el.start" class="entry-wrapper">
    <div v-if="shouldDisplaySpeakerLabel(el.speaker, index)" class="entry-speaker" data-testid="transcription-speaker-label">
      {{ speakerLabel(el.speaker!) }}
    </div>
    <dt class="entry-time">
      <a @click.stop.prevent="store.setVideoTime(el.start)" href="#" :data-testid="`viewer-transcription-at-${el.start}`" class="entry-time-link">
        {{ secsToHuman(el.start) }}
      </a>
    </dt>
    <TranscriptionEdit v-if="editing" :transcription="el" />
    <dd data-testid="transcription-list-item" v-else class="group flex flex-1 justify-between">
      <p class="dark:text-zinc-300">{{ el.words }}</p>

      <!-- Action Buttons -->
      <div class="opacity-0 group-hover:opacity-100 transition-opacity flex gap-1">
        <ButtonClipboard v-bind="getCopyConfig(el.words)" />
        <ButtonClipboard v-bind="getShareConfig(el.start)" />
      </div>
    </dd>
  </dl>
</template>

<script lang="ts" setup>
import { computed } from 'vue';
import { storeToRefs } from 'pinia';
import { useViewerItemStore } from '@/modules/viewer/store/useViewerItemStore';
import { useTranscriptionStore } from '@/modules/viewer/store/useTranscriptionStore';
import { TrackEvents } from '@/composables/useTrack';
import TranscriptionEdit from './TranscriptionEdit.vue';
import ButtonClipboard from '../../../meta_buttons/ButtonClipboard.vue';

withDefaults(
  defineProps<{
    editing?: boolean;
  }>(),
  {
    editing: false,
  }
);
const store = useViewerItemStore();
const { transcriptionData: list } = storeToRefs(useTranscriptionStore());
const hasNamedSpeaker = computed(() => list.value.some(segment => /^S\d+$/.test(segment.speaker || '')));

const shouldDisplaySpeakerLabel = (speaker: string | undefined, index: number) => {
  return Boolean(speaker && hasNamedSpeaker.value && (index === 0 || list.value[index - 1].speaker !== speaker));
};

// Copy button configuration
const getCopyConfig = (text: string) => ({
  icon: 'general.copyFileFolder',
  tooltip: 'Copy text',
  dataTestId: 'transcription-item-copy',
  content: text,
  successMessage: 'Text copied to clipboard',
  trackingEvent: TrackEvents.VIEWER_TRANSCRIPTION_BLOCK_COPY,
});

// Share button configuration
const getShareConfig = (startTime: number) => ({
  icon: 'general.copy',
  tooltip: 'Copy share link',
  dataTestId: 'transcription-item-share',
  content: store.getTimestampedShareLink(startTime),
  successMessage: 'Share link copied to clipboard',
  trackingEvent: TrackEvents.VIEWER_TRANSCRIPTION_BLOCK_SHARE,
});

const secsToHuman = (secs: number) => {
  const t = secs % 3600;
  const m = Math.floor(t / 60);
  const s = Math.floor(t % 60);

  return `${m.toLocaleString('en-US', { minimumIntegerDigits: 2 })}:${s.toLocaleString('en-US', { minimumIntegerDigits: 2 })}`;
};

const speakerLabel = (speaker: string) => {
  if (speaker === 'UU') return 'Unknown speaker';
  const match = speaker.match(/^S(\d+)$/);
  return match ? `Speaker ${match[1]}` : 'Unknown speaker';
};
</script>
<style scoped>
/* flex-wrap lets the speaker label claim a full-width row of its own above the
   timestamp/text columns; gap-y stays small so it reads as a heading for the
   lines beneath it rather than a detached row. */
.entry-wrapper {
  @apply relative flex flex-wrap gap-x-8 gap-y-1 text-base;
}

.entry-speaker {
  @apply w-full text-sm font-semibold;
  color: #9557af;
}

.dark .entry-speaker {
  color: #e0adff;
}

.entry-time {
  @apply text-slate-600 dark:text-zinc-400 w-max;
}
.entry-time-link {
  @apply relative hover:after:content-['→'] hover:text-blue-600 after:absolute rounded focus-visible:ring focus-visible:ring-blue-500/50 focus-visible:bg-blue-500/10 focus:outline-none;
}
</style>
